<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { BottomNav, DemoBanner } from '@pinchcrew/ui'
import { useAuthStore } from './stores/auth'
import { useShiftsStore } from './stores/shifts'
import { usingMockApi } from '@pinchcrew/core'
import { t, locale } from './i18n'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const shifts = useShiftsStore()

const showNav = computed(() => auth.isAuthenticated && !!route.meta.tab)
const currentTab = computed(() => (route.meta.tab as string) ?? '')

const navItems = computed(() => [
  { value: 'shifts', label: t('nav.shifts') },
  { value: 'my-shifts', label: t('nav.myShifts'), badge: shifts.upcoming.length || undefined },
  { value: 'learn', label: t('nav.learn') },
  { value: 'earnings', label: t('nav.earnings') },
  { value: 'profile', label: t('nav.profile') },
])

function go(tab: string) {
  router.push(`/${tab}`)
}
</script>

<template>
  <!-- `locale` in the key forces a re-render on language switch so every
       t() call re-evaluates, including ones inside child component props. -->
  <div :key="locale" class="app" :class="{ 'app--with-nav': showNav, 'app--demo': usingMockApi }">
    <DemoBanner v-if="usingMockApi" />
    <main class="app__main">
      <RouterView />
    </main>

    <BottomNav
      v-if="showNav"
      :model-value="currentTab"
      :items="navItems"
      @update:model-value="go"
    >
      <template #icon-shifts>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="3" y="6" width="18" height="15" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" stroke-linecap="round" />
        </svg>
      </template>
      <template #icon-my-shifts>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8">
          <circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </template>
      <template #icon-learn>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M3 6.5l9-3 9 3-9 3-9-3z" stroke-linejoin="round" /><path d="M6.5 9.8V15c0 1.2 2.6 2.8 5.5 2.8s5.5-1.6 5.5-2.8V9.8" stroke-linecap="round" />
        </svg>
      </template>
      <template #icon-earnings>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="2.5" y="6" width="19" height="13" rx="2" /><path d="M2.5 10.5h19" />
        </svg>
      </template>
      <template #icon-profile>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8">
          <circle cx="12" cy="8.5" r="3.8" /><path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" stroke-linecap="round" />
        </svg>
      </template>
    </BottomNav>
  </div>
</template>

<style scoped>
.app { min-height: 100dvh; background: var(--k-color-bg); }
/* Clear the fixed demo strip so headers are not hidden behind it. */
.app--demo { padding-top: var(--k-layout-demo-banner-height); }
.app__main { max-width: var(--k-control-content-max-width); margin: 0 auto; }
/* Clear the fixed bottom nav plus the iOS home indicator. */
.app--with-nav .app__main { padding-bottom: calc(var(--k-control-bottom-nav-height) + env(safe-area-inset-bottom)); }
</style>
